/**
 * L'accès à la base pour le brouillon de facturation de la garderie : les écoles
 * qui ont un catalogue, les mois d'une école, l'enregistrement d'un mois
 * (période, jours d'école du midi, période offerte) et le calcul du brouillon
 * (`garderie_brouillon`, migration `20261101090000_le_brouillon_de_la_garderie`).
 */
import { db } from '../payments/db';
import type { Brouillon, FactureEmise, MoisDeGarderie, ResultatDeLEmission } from './brouillon';

export interface EcoleAvecCatalogue {
  centerId: string;
  nom: string;
  tag: string;
  anneeScolaire: number;
  debut: string;
  fin: string;
  prixMidiCents: number;
}

export async function lireLesEcolesAvecCatalogue(): Promise<EcoleAvecCatalogue[]> {
  const { data, error } = await db
    .from('garderie_annees')
    .select('center_id, annee_scolaire, debut, fin, prix_midi_plein_cents, centre:centers(name, tag)')
    .order('annee_scolaire', { ascending: false });
  if (error) throw error;
  type Ligne = { center_id: string; annee_scolaire: number; debut: string; fin: string; prix_midi_plein_cents: number; centre: { name: string; tag: string } | { name: string; tag: string }[] | null };
  return ((data ?? []) as Ligne[])
    .map((l) => {
      const centre = Array.isArray(l.centre) ? l.centre[0] : l.centre;
      return {
        centerId: l.center_id,
        nom: centre?.name ?? l.center_id,
        tag: centre?.tag ?? '',
        anneeScolaire: l.annee_scolaire,
        debut: l.debut,
        fin: l.fin,
        prixMidiCents: l.prix_midi_plein_cents,
      };
    })
    .sort((a, b) => a.nom.localeCompare(b.nom));
}

export async function lireLesMois(centerId: string): Promise<MoisDeGarderie[]> {
  const { data, error } = await db
    .from('garderie_mois')
    .select('id, center_id, mois, periode_du, periode_au, jours_ecole_midi, offert_du, offert_au, etat, facture_le, notes')
    .eq('center_id', centerId)
    .order('mois');
  if (error) throw error;
  return (data ?? []) as MoisDeGarderie[];
}

/** Crée ou met à jour le mois (clé : centre + mois). Un mois facturé refuse tout changement : la base le dit. */
export async function enregistrerLeMois(m: Omit<MoisDeGarderie, 'id' | 'etat' | 'facture_le'>): Promise<MoisDeGarderie> {
  const { data, error } = await db
    .from('garderie_mois')
    .upsert(
      {
        center_id: m.center_id,
        mois: m.mois,
        periode_du: m.periode_du,
        periode_au: m.periode_au,
        jours_ecole_midi: m.jours_ecole_midi,
        offert_du: m.offert_du,
        offert_au: m.offert_au,
        notes: m.notes ?? null,
      },
      { onConflict: 'center_id,mois' },
    )
    .select('id, center_id, mois, periode_du, periode_au, jours_ecole_midi, offert_du, offert_au, etat, facture_le, notes')
    .single();
  if (error) throw error;
  return data as MoisDeGarderie;
}

export async function calculerLeBrouillon(centerId: string, mois: string): Promise<Brouillon> {
  const { data, error } = await db.rpc('garderie_brouillon', { p_center_id: centerId, p_mois: mois });
  if (error) throw error;
  return data as Brouillon;
}

/* ------------------------------------------------------------------------ */
/*  L'émission (migration 20261101220000)                                    */
/* ------------------------------------------------------------------------ */

/**
 * Émet les factures du mois : la base recalcule le brouillon, le gèle, numérote, ferme le
 * mois. Administration seulement ; un mois déjà facturé ou un brouillon avec avertissements
 * non assumés sont refusés par la base, qui le dit.
 */
export async function emettreLeMois(centerId: string, mois: string, annotation: string, malgreLesAvertissements: boolean): Promise<ResultatDeLEmission> {
  const { data, error } = await db.rpc('garderie_emettre_mois', {
    p_center_id: centerId,
    p_mois: mois,
    p_annotation: annotation,
    p_malgre_avertissements: malgreLesAvertissements,
    p_echeance_jours: 20,
  });
  if (error) throw error;
  return data as ResultatDeLEmission;
}

export async function lireLesFactures(centerId: string, mois: string): Promise<FactureEmise[]> {
  const { data, error } = await db
    .from('garderie_factures')
    .select(
      'id, famille_id, nom, prenom, email, statut_tarifaire, communication, montant_cents, a_confirmer_cents, sans_abonnement, annotation, emise_le, '
        + 'facture:invoices(invoice_number, communication, due_date, status, pdf_url, invoice_email_sent_at), '
        + 'lignes:garderie_factures_lignes(enfant_id)',
    )
    .eq('center_id', centerId)
    .eq('mois', mois)
    .order('nom')
    .order('prenom');
  if (error) throw error;
  type Facture = { invoice_number: string; communication: string; due_date: string | null; status: string; pdf_url: string | null; invoice_email_sent_at: string | null };
  type Lu = {
    id: string; famille_id: string; nom: string; prenom: string | null; email: string | null; statut_tarifaire: FactureEmise['statut'];
    communication: string; montant_cents: number; a_confirmer_cents: number; sans_abonnement: boolean; annotation: string | null; emise_le: string;
    facture: Facture | Facture[] | null; lignes: { enfant_id: string }[] | null;
  };
  return ((data ?? []) as unknown as Lu[]).map((l) => {
    const f = Array.isArray(l.facture) ? l.facture[0] : l.facture;
    const lignes = l.lignes ?? [];
    return {
      id: l.id,
      famille_id: l.famille_id,
      nom: l.nom,
      prenom: l.prenom,
      email: l.email,
      statut: l.statut_tarifaire,
      communication: l.communication,
      montant_cents: l.montant_cents,
      a_confirmer_cents: l.a_confirmer_cents,
      sans_abonnement: l.sans_abonnement,
      annotation: l.annotation,
      emise_le: l.emise_le,
      numero: f?.invoice_number ?? '',
      communication_imprimee: f?.communication ?? '',
      echeance: f?.due_date ?? null,
      statut_facture: f?.status ?? '',
      pdf_url: f?.pdf_url ?? null,
      courriel_envoye_le: f?.invoice_email_sent_at ?? null,
      enfants: new Set(lignes.map((x) => x.enfant_id)).size,
      lignes: lignes.length,
    };
  });
}

/** « Rentre à la maison le midi » sur la fiche de l'enfant : plus rien n'est facturé pour le midi. Administration. */
export async function poserRentreLeMidi(enfantId: string, valeur: boolean): Promise<void> {
  const { error } = await db.from('garderie_enfants').update({ rentre_le_midi: valeur }).eq('id', enfantId);
  if (error) throw error;
}
